import React, { useEffect, useMemo, useState, useCallback } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    List,
    Stack,
    Typography,
    CircularProgress,
} from '@mui/material'
import {
    Send as SendIcon,
    AccountBalance as BankIcon,
    Receipt as BillIcon,
    Favorite as DonateIcon,
    ConfirmationNumber as TicketIcon,
    Group as GroupIcon,
    QrCode as PaymentIcon,
    ReceiptLong as ProofIcon,
    AddCard as AddMoneyIcon,
    ArrowOutward as ArrowOutwardIcon,
    SouthWest as SouthWestIcon,
    History as HistoryIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import RecentTransaction from '../components/dashboard/RecentTransaction'
import PromotionCarousel from '../components/promotions/PromotionCarousel'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import api from '../services/api'
import { mapDashboardSummary } from '../mappers/dashboardMapper'

const safeDefault = {
    wallets: [],
    stats: {
        totalBalance: 0,
        totalSent: 0,
        totalReceived: 0,
        transactions: 0,
    },
    recentTransactions: [],
    chart: [],
}

const diasporaCountryCodes = ['US', 'CA', 'GB', 'AU', 'FR', 'EU']

const formatMoney = (amount, currency = 'USD') => {
    const numeric = Number(amount || 0)
    const resolvedCurrency = String(currency || 'USD').toUpperCase()

    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: resolvedCurrency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numeric)
    } catch (error) {
        return `${resolvedCurrency} ${numeric.toFixed(2)}`
    }
}

const pickPrimaryWallet = (wallets = [], fallbackCurrency = 'USD') => {
    if (Array.isArray(wallets) && wallets.length > 0) {
        const preferred = wallets.find(
            (wallet) => String(wallet.currency || '').toUpperCase() === fallbackCurrency
        )
        return preferred || wallets[0]
    }

    return {
        currency: fallbackCurrency,
        balance: 0,
    }
}

const Dashboard = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { t, lang } = useLanguage()

    const [loading, setLoading] = useState(true)
    const [usingFallback, setUsingFallback] = useState(false)
    const [data, setData] = useState(safeDefault)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const res = await api.get('/dashboard/summary')
            const mapped = mapDashboardSummary(res.data)

            setData({
                wallets: mapped?.wallets || safeDefault.wallets,
                stats: mapped?.stats || safeDefault.stats,
                recentTransactions: mapped?.recentTransactions || safeDefault.recentTransactions,
                chart: mapped?.chart || safeDefault.chart,
            })
            setUsingFallback(false)
        } catch (error) {
            setData(safeDefault)
            setUsingFallback(true)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const displayName = user?.firstName || user?.name || 'MojoPayr User'
    const countryCode = String(user?.country || 'US').toUpperCase()
    const isDiaspora = diasporaCountryCodes.includes(countryCode)
    const baseCurrency = user?.baseCurrency || (isDiaspora ? 'USD' : 'SLE')
    const primaryWallet = useMemo(
        () => pickPrimaryWallet(data.wallets, baseCurrency),
        [data.wallets, baseCurrency]
    )

    const actionItems = [
        { label: t('nav.send', null, 'Send Money'), icon: <SendIcon />, path: '/send' },
        { label: t('dashboard.quick.payment', null, 'Payment'), icon: <PaymentIcon />, path: '/payment' },
        { label: t('dashboard.quick.bank', null, 'Bank'), icon: <BankIcon />, path: '/bank-transfers' },
        { label: t('dashboard.quick.payBill', null, 'Pay Bill'), icon: <BillIcon />, path: '/pay-bills' },
        { label: t('dashboard.quick.donate', null, 'Donate'), icon: <DonateIcon />, path: '/donations' },
        { label: t('dashboard.quick.tickets', null, 'Tickets'), icon: <TicketIcon />, path: '/tickets' },
        { label: t('dashboard.quick.contribution', null, 'Contribution/Osusu'), icon: <GroupIcon />, path: '/contributions' },
        { label: t('dashboard.quick.proofCenter', null, 'Proof Center'), icon: <ProofIcon />, path: '/transactions' },
    ]

    const residentLabel = isDiaspora
        ? (lang === 'fr' ? 'Portefeuille diaspora' : 'Diaspora Wallet')
        : (lang === 'fr' ? 'Portefeuille local' : 'Local Wallet')
    const residentNote = isDiaspora
        ? (lang === 'fr'
            ? `Vous payez depuis ${countryCode} et reglez les services au pays avec le taux de change et les frais MojoPayr.`
            : `You pay from ${countryCode} and settle services back home with MojoPayr FX and fees.`)
        : (lang === 'fr'
            ? 'Votre tableau de bord web reste leger, local et utile pour les paiements et encaissements du quotidien.'
            : 'Your web dashboard stays light, local, and useful for daily payments and collections.')

    const quickStats = [
        {
            title: lang === 'fr' ? 'Envoye' : 'Sent',
            value: formatMoney(data.stats.totalSent, primaryWallet.currency),
            subtitle: lang === 'fr' ? 'Argent envoye' : 'Money moved out',
            icon: <ArrowOutwardIcon fontSize="small" />,
        },
        {
            title: lang === 'fr' ? 'Recu' : 'Received',
            value: formatMoney(data.stats.totalReceived, primaryWallet.currency),
            subtitle: lang === 'fr' ? 'Argent recu' : 'Money received',
            icon: <SouthWestIcon fontSize="small" />,
        },
        {
            title: t('nav.transactions', null, 'Transactions'),
            value: String(data.stats.transactions || 0),
            subtitle: lang === 'fr' ? 'Activite enregistree' : 'Recorded activity',
            icon: <HistoryIcon fontSize="small" />,
        },
    ]

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '70vh',
                }}
            >
                <CircularProgress size={52} />
            </Box>
        )
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
            <Stack spacing={3}>
                <Box>
                    <Typography
                        sx={{
                            fontSize: { xs: 30, md: 44 },
                            fontWeight: 900,
                            lineHeight: 1.08,
                            letterSpacing: '-0.03em',
                        }}
                    >
                        {lang === 'fr' ? 'Bon retour, ' : 'Welcome back, '}{displayName}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', mt: 1, maxWidth: 760 }}>
                        {residentNote}
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Stack spacing={3}>
                            <Card
                                sx={{
                                    borderRadius: 6,
                                    overflow: 'hidden',
                                    background:
                                        'linear-gradient(135deg, rgba(42,27,102,1) 0%, rgba(91,46,255,1) 100%)',
                                    boxShadow: '0 28px 60px rgba(49,26,137,0.38)',
                                }}
                            >
                                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                    <Stack spacing={2.5}>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            justifyContent="space-between"
                                            spacing={2}
                                        >
                                            <Box>
                                                <Typography sx={{ color: 'rgba(255,255,255,0.74)', mb: 1 }}>
                                                    {residentLabel}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: '#FFFFFF',
                                                        fontWeight: 900,
                                                        fontSize: { xs: 34, md: 44 },
                                                        letterSpacing: '-0.03em',
                                                    }}
                                                >
                                                    {formatMoney(primaryWallet.balance, primaryWallet.currency)}
                                                </Typography>
                                            </Box>

                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                <Chip
                                                    label={String(primaryWallet.currency || baseCurrency).toUpperCase()}
                                                    sx={{
                                                        color: '#FFFFFF',
                                                        bgcolor: 'rgba(255,255,255,0.14)',
                                                    }}
                                                />
                                                <Chip
                                                    label={isDiaspora
                                                        ? (lang === 'fr' ? 'Pret pour la diaspora' : 'Cross-border ready')
                                                        : (lang === 'fr' ? 'Pret en local' : 'Local ready')}
                                                    sx={{
                                                        color: '#FFFFFF',
                                                        bgcolor: 'rgba(255,255,255,0.14)',
                                                    }}
                                                />
                                                <Chip
                                                    label={usingFallback
                                                        ? (lang === 'fr' ? 'Vue limitee' : 'Limited view')
                                                        : (lang === 'fr' ? 'Portefeuille actif' : 'Live wallet')}
                                                    sx={{
                                                        color: '#FFFFFF',
                                                        bgcolor: 'rgba(255,255,255,0.14)',
                                                    }}
                                                />
                                            </Stack>
                                        </Stack>

                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                            <Button
                                                variant="contained"
                                                startIcon={<AddMoneyIcon />}
                                                onClick={() => navigate('/wallet')}
                                                sx={{
                                                    bgcolor: '#FFFFFF',
                                                    color: '#0B0620',
                                                    fontWeight: 900,
                                                    borderRadius: 4,
                                                    textTransform: 'none',
                                                    px: 3,
                                                    '&:hover': {
                                                        bgcolor: '#F7F7FF',
                                                    },
                                                }}
                                            >
                                                {lang === 'fr' ? 'Ajouter de l argent' : 'Add Money'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                startIcon={<ProofIcon />}
                                                onClick={() => navigate('/transactions')}
                                                sx={{
                                                    color: '#FFFFFF',
                                                    borderColor: 'rgba(255,255,255,0.25)',
                                                    fontWeight: 800,
                                                    borderRadius: 4,
                                                    textTransform: 'none',
                                                    px: 3,
                                                }}
                                            >
                                                {lang === 'fr' ? 'Centre de preuves' : 'Proof Center'}
                                            </Button>
                                        </Stack>

                                        <PromotionCarousel
                                            platform="website"
                                            country={countryCode}
                                            currency={primaryWallet.currency || baseCurrency}
                                            userType={isDiaspora ? 'DIASPORA' : 'LOCAL'}
                                            fallbackTitle={lang === 'fr' ? 'Portefeuille MojoPayr' : 'MojoPayr Wallet'}
                                            fallbackMessage={lang === 'fr'
                                                ? 'Ajoutez des fonds ou envoyez directement vers un autre wallet MojoPayr.'
                                                : 'Top up fast or move value directly into another MojoPayr wallet.'}
                                            compact
                                        />
                                    </Stack>
                                </CardContent>
                            </Card>

                            <Card
                                sx={{
                                    borderRadius: 6,
                                    background: 'rgba(19,24,43,0.96)',
                                    border: '1px solid rgba(255,255,255,0.06)',
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Typography sx={{ fontWeight: 800, mb: 1.2 }}>
                                        {lang === 'fr' ? 'Prochaines actions utiles' : 'Next Best Actions'}
                                    </Typography>
                                    <Typography sx={{ color: 'text.secondary', mb: 2.2 }}>
                                        {lang === 'fr'
                                            ? 'Gardez le premier ecran utile : approvisionnez votre portefeuille, envoyez au pays, ou payez un service sans fouiller dans le site.'
                                            : 'Keep the first screen useful: fund your wallet, send back home, or pay directly for a service without digging through the site.'}
                                    </Typography>

                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12} sm={4}>
                                            <Box
                                                sx={{
                                                    p: 1.8,
                                                    borderRadius: 4,
                                                    bgcolor: 'rgba(255,255,255,0.03)',
                                                    border: '1px solid rgba(255,255,255,0.05)',
                                                    height: '100%',
                                                }}
                                            >
                                                <Typography sx={{ color: '#FFFFFF', fontWeight: 800, mb: 0.6 }}>
                                                    {t('nav.send', null, 'Send Money')}
                                                </Typography>
                                                <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 1.5 }}>
                                                    {lang === 'fr'
                                                        ? 'Envoi transfrontalier avec taux et frais clairs.'
                                                        : 'Cross-border send with clear FX and fees.'}
                                                </Typography>
                                                <Button
                                                    variant="text"
                                                    onClick={() => navigate('/send')}
                                                    sx={{
                                                        px: 0,
                                                        minWidth: 0,
                                                        textTransform: 'none',
                                                        fontWeight: 900,
                                                        color: '#FF8A00',
                                                    }}
                                                >
                                                    {lang === 'fr' ? 'Ouvrir' : 'Open'}
                                                </Button>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} sm={4}>
                                            <Box
                                                sx={{
                                                    p: 1.8,
                                                    borderRadius: 4,
                                                    bgcolor: 'rgba(255,255,255,0.03)',
                                                    border: '1px solid rgba(255,255,255,0.05)',
                                                    height: '100%',
                                                }}
                                            >
                                                <Typography sx={{ color: '#FFFFFF', fontWeight: 800, mb: 0.6 }}>
                                                    {lang === 'fr' ? 'Payer un commerce' : 'Pay a Business'}
                                                </Typography>
                                                <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 1.5 }}>
                                                    {lang === 'fr'
                                                        ? 'Entrez d abord le montant local et payez directement.'
                                                        : 'Enter the local amount first and pay directly.'}
                                                </Typography>
                                                <Button
                                                    variant="text"
                                                    onClick={() => navigate('/payment')}
                                                    sx={{
                                                        px: 0,
                                                        minWidth: 0,
                                                        textTransform: 'none',
                                                        fontWeight: 900,
                                                        color: '#FF8A00',
                                                    }}
                                                >
                                                    {lang === 'fr' ? 'Ouvrir' : 'Open'}
                                                </Button>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} sm={4}>
                                            <Box
                                                sx={{
                                                    p: 1.8,
                                                    borderRadius: 4,
                                                    bgcolor: 'rgba(255,255,255,0.03)',
                                                    border: '1px solid rgba(255,255,255,0.05)',
                                                    height: '100%',
                                                }}
                                            >
                                                <Typography sx={{ color: '#FFFFFF', fontWeight: 800, mb: 0.6 }}>
                                                    {lang === 'fr' ? 'Payer des factures' : 'Pay Bills'}
                                                </Typography>
                                                <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 1.5 }}>
                                                    {lang === 'fr'
                                                        ? 'Services, frais scolaires et besoins essentiels au pays.'
                                                        : 'Utilities, school fees, and back-home essentials.'}
                                                </Typography>
                                                <Button
                                                    variant="text"
                                                    onClick={() => navigate('/pay-bills')}
                                                    sx={{
                                                        px: 0,
                                                        minWidth: 0,
                                                        textTransform: 'none',
                                                        fontWeight: 900,
                                                        color: '#FF8A00',
                                                    }}
                                                >
                                                    {lang === 'fr' ? 'Ouvrir' : 'Open'}
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card
                            sx={{
                                borderRadius: 6,
                                height: '100%',
                                background: 'rgba(19,24,43,0.96)',
                                border: '1px solid rgba(255,255,255,0.06)',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ fontWeight: 800, mb: 2 }}>
                                    {lang === 'fr' ? 'Apercu du portefeuille' : 'Wallet Snapshot'}
                                </Typography>
                                <Stack spacing={1.5}>
                                    {quickStats.map((item) => (
                                        <Box
                                            key={item.title}
                                            sx={{
                                                p: 1.8,
                                                borderRadius: 4,
                                                bgcolor: 'rgba(255,255,255,0.03)',
                                                border: '1px solid rgba(255,255,255,0.05)',
                                            }}
                                        >
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                                    {item.title}
                                                </Typography>
                                                <Box sx={{ color: '#FF8A00' }}>{item.icon}</Box>
                                            </Stack>
                                            <Typography sx={{ fontWeight: 900, fontSize: 20, mt: 1 }}>
                                                {item.value}
                                            </Typography>
                                            <Typography sx={{ color: 'text.secondary', fontSize: 12, mt: 0.5 }}>
                                                {item.subtitle}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Card
                    sx={{
                        borderRadius: 6,
                        background: 'rgba(19,24,43,0.96)',
                        border: '1px solid rgba(255,255,255,0.06)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                        <Typography sx={{ fontWeight: 800, mb: 2.5 }}>
                            {lang === 'fr' ? 'Actions rapides' : 'Quick Actions'}
                        </Typography>
                        <Grid container spacing={2.5}>
                            {actionItems.map((action) => (
                                <Grid item xs={6} sm={4} md={3} key={action.label}>
                                    <Stack
                                        spacing={1.25}
                                        alignItems="center"
                                        onClick={() => navigate(action.path)}
                                        sx={{
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: { xs: 64, md: 72 },
                                                height: { xs: 64, md: 72 },
                                                borderRadius: '50%',
                                                display: 'grid',
                                                placeItems: 'center',
                                                background: 'linear-gradient(135deg, #241A47, #33206D)',
                                                color: '#FF8A00',
                                                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04)',
                                                transition: 'transform 0.18s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                },
                                            }}
                                        >
                                            {action.icon}
                                        </Box>
                                        <Typography
                                            sx={{
                                                color: '#D8D2F6',
                                                fontSize: { xs: 12, sm: 13 },
                                                fontWeight: 600,
                                                textAlign: 'center',
                                            }}
                                        >
                                            {action.label}
                                        </Typography>
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <Card
                            sx={{
                                borderRadius: 6,
                                height: '100%',
                                background: 'rgba(19,24,43,0.96)',
                                border: '1px solid rgba(255,255,255,0.06)',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                                    {lang === 'fr' ? 'Activite et recus' : 'Activity & Receipts'}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', mb: 2.5 }}>
                                    {lang === 'fr'
                                        ? 'Gardez recus, transferts, factures, dons et preuves de paiement au meme endroit sans charger des analyses trop lourdes.'
                                        : 'Keep receipts, transfers, bills, donations, and payment proof in one place without loading heavy analytics first.'}
                                </Typography>

                                <Stack spacing={1.5}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 4,
                                            bgcolor: 'rgba(255,255,255,0.03)',
                                            border: '1px solid rgba(255,255,255,0.05)',
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                                            {usingFallback
                                                ? (lang === 'fr' ? 'Vue d activite limitee' : 'Limited activity view')
                                                : (lang === 'fr' ? 'Flux d activite en direct' : 'Live activity feed')}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
                                            {usingFallback
                                                ? (lang === 'fr'
                                                    ? 'Le resume backend n est pas disponible pour le moment, donc le tableau de bord affiche une vue limitee au lieu de donner une fausse impression de donnees en direct.'
                                                    : 'Backend summary is unavailable right now, so the dashboard shows a limited view until live data is available.')
                                                : (lang === 'fr'
                                                    ? 'Votre activite recente de portefeuille et de paiement continuera d apparaitre ici.'
                                                    : 'Your latest wallet and payment activity will continue to appear here.')}
                                        </Typography>
                                    </Box>

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<ProofIcon />}
                                            onClick={() => navigate('/transactions')}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                color: '#FFFFFF',
                                                borderColor: 'rgba(255,255,255,0.14)',
                                                borderRadius: 4,
                                                textTransform: 'none',
                                                fontWeight: 800,
                                            }}
                                        >
                                            {lang === 'fr' ? 'Ouvrir le centre de preuves' : 'Open Proof Center'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<PaymentIcon />}
                                            onClick={() => navigate('/payment')}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                color: '#FFFFFF',
                                                borderColor: 'rgba(255,255,255,0.14)',
                                                borderRadius: 4,
                                                textTransform: 'none',
                                                fontWeight: 800,
                                            }}
                                        >
                                            {lang === 'fr' ? 'Faire un paiement' : 'Make Payment'}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Card
                            sx={{
                                borderRadius: 6,
                                height: '100%',
                                background: 'rgba(19,24,43,0.96)',
                                border: '1px solid rgba(255,255,255,0.06)',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ fontWeight: 800, mb: 2 }}>
                                    {lang === 'fr' ? 'Transactions recentes' : 'Recent Transactions'}
                                </Typography>
                                <List sx={{ p: 0 }}>
                                    {data.recentTransactions.length === 0 ? (
                                        <Typography sx={{ color: 'text.secondary' }}>
                                            {lang === 'fr'
                                                ? 'Aucune transaction pour le moment. Votre historique apparaitra ici.'
                                                : 'No transactions yet. Your payment trail will show here.'}
                                        </Typography>
                                    ) : (
                                        data.recentTransactions.map((tx) => (
                                            <RecentTransaction key={tx.id} transaction={tx} />
                                        ))
                                    )}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Stack>
        </Container>
    )
}

export default Dashboard

